import React, { FC, useCallback, useState } from "react";
import clsx from "clsx";

import VideoJS from "../../../../atoms/VideoJS";
import { ReactComponent as ComingSoonIcon } from "../../../../assets/svg/coming_soon.svg";
import { NavigationVideoTeachingMaterial } from "../../../../state";

import styles from "./LessonVideo.module.scss";

type Lesson = NavigationVideoTeachingMaterial["lessons"][number];

type Props = {
  lesson: Lesson;
  isPlaying: boolean;
  onVideoPlay: (lessonId: number) => void;
};

const LessonVideo: FC<Props> = ({ lesson, isPlaying, onVideoPlay }) => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [shouldAutoPlayNext, setShouldAutoPlayNext] = useState(false);
  const currentChapter = lesson.video_chapters[currentChapterIndex];
  const sourceUrl = currentChapter
    ? process.env.NODE_ENV === "development"
      ? `${process.env.REACT_APP_VIDEO_LESSON_BASE_URL}${currentChapter.url}`
      : currentChapter.url
    : null;

  const handleVideoEnd = useCallback(() => {
    const nextChapterIndex = currentChapterIndex + 1;

    if (nextChapterIndex < lesson.video_chapters.length) {
      setCurrentChapterIndex(nextChapterIndex);
      return;
    }

    setShouldAutoPlayNext(false);
  }, [currentChapterIndex, lesson.video_chapters.length]);

  const handleVideoStart = useCallback(() => {
    setShouldAutoPlayNext(true);
    onVideoPlay(lesson.id);
  }, [lesson.id, onVideoPlay]);

  return (
    <div className={styles.lesson}>
      {sourceUrl && currentChapter ? (
        <div className={styles.screen}>
          <div className={styles.chapterList}>
            <span className={styles.chapterLabel}>チャプター</span>
            {lesson.video_chapters.map((chapter, index) => (
              <button
                aria-pressed={index === currentChapterIndex}
                className={clsx(styles.chapterButton, {
                  [styles.isActive]: index === currentChapterIndex,
                })}
                key={chapter.id}
                onClick={() => setCurrentChapterIndex(index)}
                type="button"
              >
                {index + 1}
              </button>
            ))}
          </div>
          <VideoJS
            className={styles.video}
            onVideoEnd={handleVideoEnd}
            onVideoStart={handleVideoStart}
            playing={isPlaying && shouldAutoPlayNext}
            showVideo={true}
            sourceUrl={sourceUrl}
          />
        </div>
      ) : (
        <ComingSoonIcon className={styles.comingSoon} />
      )}
      <p className={styles.lessonName}>{lesson.name}</p>
    </div>
  );
};

export default LessonVideo;
